import clsx from 'clsx';
import { DropdownIcon } from '@/components/icons/svg-icons';
import { BetMsgModel, BetslipStyle, BETTING_CODE, CanBetResultModel, ParlayTicketModel } from '@/hooks/query';
import { useBetslipStore } from '@/store/betslip';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { useTranslations } from 'next-intl';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { BetInput } from '../bet-input';
import { BetKeypad } from '../bet-keypad'
import { Notice, TextErrorParlay } from '../bet-msg';
import { BetTicketParlay } from '../bet-ticket-parlay';
import { BetSlipParlayPayout } from '../payout';
import { StakePerBet } from '../stake-per-bet';
import { betslipStyle } from '@/features/betslip/betslip.style';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import { useAccountInfo } from '@/hooks/query/account';
import { useFloatBetslip } from '@/features/betslip/hook/use-float-betslip';
import { removeCommas } from '@/utils/odds-utils';

interface ParlayAreaProps {
	canBetResult: CanBetResultModel;
}

interface ParlayProps {
	isParlayExpand: boolean;
	setIsExpand: Dispatch<SetStateAction<boolean>>;
	minBet: string | undefined;
	parlayTickets: Array<ParlayTicketModel>;
	canBetResults: BetMsgModel[];
}

interface SystemParlayProps {
	minBet: string | undefined;
	parlayTickets: Array<ParlayTicketModel>;
	canBetResults: BetMsgModel[];
}

export const ParlayArea = ({ canBetResult }: ParlayAreaProps) => {
	const t = useTranslations('General');
	const [
		isExpand,
		currentParlayTicketModel,
		betslipTab
	] = useBetslipStore(state => [
		state.isExpand,
		state.currentParlayTicketModel,
		state.betslipTab
	]);

	const { user } = useAccountInfo();
	const { isIndependentMode } = useBetslipContext()
	const { activeFloatBetSlip } = useFloatBetslip();
	const view = useAppearanceStore(store => store.view);

	const defaultIsExpand = (activeFloatBetSlip && view === ViewEnum.mars) ? false : isExpand;
	// 分開投注情況下的parlay tab不管怎樣預設展開
	const [isParlayExpand, setIsExpand] = useState(defaultIsExpand || (isIndependentMode && betslipTab === 'parlay'));

	const innerMsg = canBetResult.innerMsg;
	const minBet =  removeCommas(currentParlayTicketModel?.MinBet ?? '');
	const parlayTickets = currentParlayTicketModel?.comboData ?? new Array<ParlayTicketModel>;

	const toggleIsExpand = () => {
		setIsExpand(!isParlayExpand);
	}

	useEffect(() => {
		if (!activeFloatBetSlip) return;

		// 當 odds change 時自動收合 parlay 區塊
		const isOddsChange = canBetResult.outerMsg.some(msg => msg.code === 15);
		if (isOddsChange) {
			setIsExpand(false);
		}
	}, [canBetResult.outerMsg]);


	return (
		<>
			{/* 此元件只在float 模式顯示, mars 顯示galaxy不顯示 */}
			{activeFloatBetSlip ? (
				<div
					className={betslipStyle.parlayToggleButton}
					data-active={isParlayExpand ? true : undefined}
					onClick={toggleIsExpand}
				>
					{isParlayExpand ? t('lbl_HideSelections') : t('lbl_ShowSelections')}
					<DropdownIcon />
				</div>
			) : null}
			<div className={clsx('parlay-area', betslipStyle.parlayArea, { 
				'scrollbar': isIndependentMode,
				[betslipStyle.parlayAreaWrap]: activeFloatBetSlip,
			})}
			>
				{!activeFloatBetSlip && !isIndependentMode ? (
					<div className={betslipStyle.subTitleWrapper}>

						<div className={betslipStyle.subTitle}>
							{t('lbl_mixparlay')}
						</div>
					</div>
				) : null}
				<Parlay
					isParlayExpand={isParlayExpand}
					setIsExpand={setIsExpand}
					minBet={minBet}
					parlayTickets={parlayTickets}
					canBetResults={innerMsg ?? []}
				/>
				{
					isParlayExpand ? (
						<SystemParlay
							minBet={minBet}
							parlayTickets={parlayTickets}
							canBetResults={innerMsg ?? []}
						/>
					) : null
				}
			</div>
		</>
	);
};

const Parlay = ({
	isParlayExpand,
	setIsExpand,
	minBet,
	parlayTickets,
	canBetResults
}: ParlayProps) => {
	const t = useTranslations('General');
	const view = useAppearanceStore(state => state.view);
	const firstMixParlay = parlayTickets.find(data => data.isMixParlay);
	const { activeFloatBetSlip } = useFloatBetslip();
	if (!firstMixParlay) return null;

	const canBetResult = canBetResults.find(canBet => canBet.ticketKey === firstMixParlay.key);
	const canBet = canBetResult ? canBetResult.canBet : true;

	const toggleIsExpand = () => {
		setIsExpand(!isParlayExpand);
	}

	return (
		<div className={clsx('bet-slip-parlay', betslipStyle.betSlipParlay)}>
			<div className="bet-slip-parlay__wrap">
				<div className="bet-slip-parlay__container">
					<div className="bet-slip-parlay__header">
						<div className="bet-slip-parlay__name">{firstMixParlay.Name}</div>
						<div className="bet-slip-parlay__odds">{firstMixParlay.Odds}</div>
					</div>
				</div>
				<div className="bet-slip-parlay__stake">
					<BetInput
						type={'small'}
						isParlay={true}
						canBet={canBet}
						ticketKey={firstMixParlay.key}
						ticketMinBet={Number(minBet) || 0}
					/>
					{canBet ? <BetSlipParlayPayout payout={firstMixParlay.payout} /> : null}
					{
						canBetResult && ([BETTING_CODE.ParlayMaxBetLessThanMinBet, BETTING_CODE.ParlayNoComboAvailable] as Array<number>).includes(canBetResult?.code ?? 0) ? (
							<TextErrorParlay msg={canBetResult.msg} />
						) : null
					}
				</div>
			</div>
			<BetKeypad
				isExpand={true}
				isParlay={true}
				ticketKey={firstMixParlay.key}
				ticketStake={firstMixParlay.stake}
				ticketMaxBet={firstMixParlay.MaxBet}
			/>
			{
				canBetResult && ([BETTING_CODE.ParlayMinOdds, BETTING_CODE.ParlayComboByPool] as Array<number>).includes(canBetResult?.code ?? 0) ? (
					<Notice
						code={canBetResult.code}
						msg={canBetResult.msg}
						msgDetails={firstMixParlay.ErrorMessageDetails}
						msgResourceKey={canBetResult.msgResourceKey}
						status={canBetResult.cssStatus}
					/>
				) : null
			}
			{
				// float 模式的 mars 不顯示
				parlayTickets.length > 1 && !(activeFloatBetSlip && view === ViewEnum.mars) ? (
					<div className={clsx('bet-slip-parlay__more', betslipStyle.betSlipParlayMore)} data-expand={isParlayExpand} onClick={toggleIsExpand}>
						<span className="bet-slip-parlay__item">
							{isParlayExpand ? t('lbl_HideSelections') : t('lbl_ShowSelections')}
						</span>
						<DropdownIcon />
					</div>
				) : null
			}
		</div>
	);
}

const SystemParlay = ({
	minBet,
	parlayTickets,
	canBetResults
}: SystemParlayProps) => {
	const comboParlayCount = parlayTickets.filter(ticket => ticket.isComboParlay).length;
	const canBetComboCount = canBetResults.reduce((accumulator, betMessageModel) => {
		const ticket = parlayTickets.find(ticket => ticket.key == betMessageModel.ticketKey);
		return ticket?.isComboParlay && !betMessageModel.canBet ? --accumulator : accumulator;
	}, comboParlayCount);

	return (
		<div className="bet-slip__system-parlay">
			{
				parlayTickets.filter(ticket => !ticket.isMixParlay).map(ticket =>
					<BetTicketParlay
						key={ticket.key}
						ticket={ticket}
						ticketMinBet={Number(minBet) || 0}
						canBetResult={canBetResults.find(canBet => canBet.ticketKey === ticket.key)}
					/>
				)
			}
			{
				canBetComboCount > 1 ?
					<StakePerBet isParlay={true} /> :
					null
			}
		</div>
	);
}